/**
 * The plain-DOM screen for a boot that cannot continue (layered shell surfaces, WS-6 §3.7). It
 * runs when no surface can: the manifest, the runtime or the system bundle failed, or the root
 * crashed. It uses no framework and sets text through `textContent` only.
 */

/**
 * Which step failed. `kernel-origin`: the page setting `__ZION_KERNEL_ORIGIN__` holds a value that
 * is no kernel origin. `sign-in`: a kernel sign-in could not start or its code could not redeem
 * (kernel apps, WS-16a, design section 3.6).
 */
export type FatalReason = 'manifest' | 'runtime' | 'system-bundle' | 'root-mount' | 'kernel-origin' | 'sign-in';

/** The one sentence each reason shows under the heading. */
export const FATAL_MESSAGES: Readonly<Record<FatalReason, string>> = Object.freeze({
  manifest: 'The console could not read its module list. Reload the page to try again.',
  runtime: 'The console could not load its surface runtime. Reload the page to try again.',
  'system-bundle': 'The sign-in pages could not load. Reload the page to try again.',
  'root-mount': 'The console layout stopped working. Reload the page to try again.',
  'kernel-origin': 'The console could not find its sign-in address. Reload the page to try again.',
  'sign-in': 'The sign-in could not finish. Sign in again to continue.',
});

/** The one action a fatal screen offers: reload the page, or start a new sign-in. */
export type FatalAction = 'reload' | 'sign-in-again';

/**
 * The action of each reason. `sign-in` offers `sign-in-again`; the other five offer `reload`. A
 * new handoff starts only after a human click, so a refused code never loops: a reload of the
 * callback URL meets only `no-record`.
 */
export const FATAL_ACTIONS: Readonly<Record<FatalReason, FatalAction>> = Object.freeze({
  manifest: 'reload',
  runtime: 'reload',
  'system-bundle': 'reload',
  'root-mount': 'reload',
  'kernel-origin': 'reload',
  'sign-in': 'sign-in-again',
});

/** The button text of each action. */
const ACTION_LABELS: Readonly<Record<FatalAction, string>> = Object.freeze({ reload: 'Reload', 'sign-in-again': 'Sign in again' });

/** What a click on each action does to the window of the screen. */
const ACTION_RUNS: Readonly<Record<FatalAction, (location: Location) => void>> = Object.freeze({
  reload: (location: Location) => location.reload(),
  'sign-in-again': (location: Location) => location.assign('/'),
});

/** The heading every fatal screen shows. */
export const FATAL_HEADING = 'Something went wrong';

/**
 * Replace `el`'s children with the fatal screen for `reason`.
 *
 * Renders a `div[data-zion-fatal=<reason>][role=alert]` holding an `h1` with
 * {@link FATAL_HEADING}, a `p` with the reason's {@link FATAL_MESSAGES} entry and one button for
 * the reason's {@link FATAL_ACTIONS} entry: `Reload` calls `location.reload()` on the element's
 * window, and `Sign in again` calls `location.assign('/')` there, so the boot starts a new kernel
 * sign-in. A detached element has no window, and its button does nothing. Removes `aria-busy`
 * from `el`. Moves focus to the button, because the screen may have removed the element that held
 * focus, and a keyboard user then lands on the one action the screen offers (design-7). Logs
 * `[shell] boot stopped: <reason>` to `console.error` once per call. Never throws.
 */
export function renderFatalScreen(el: HTMLElement, reason: FatalReason): void {
  const doc = el.ownerDocument;
  const box = doc.createElement('div');
  box.setAttribute('data-zion-fatal', reason);
  box.setAttribute('role', 'alert');
  box.className = 'zion-fatal';
  const heading = doc.createElement('h1');
  heading.textContent = FATAL_HEADING;
  const message = doc.createElement('p');
  message.textContent = FATAL_MESSAGES[reason];
  const action = FATAL_ACTIONS[reason];
  const button = doc.createElement('button');
  button.type = 'button';
  button.textContent = ACTION_LABELS[action];
  button.addEventListener('click', () => {
    const location = doc.defaultView?.location;
    if (location !== undefined) ACTION_RUNS[action](location);
  });
  box.append(heading, message, button);
  el.replaceChildren(box);
  el.removeAttribute('aria-busy');
  button.focus();
  console.error(`[shell] boot stopped: ${reason}`);
}
